// Routes configuration - centralized route definitions
export const Routes = {
  // Core routes
  home: () => '/',
  users: (userId?: string) => (userId ? `/users/${userId}` : '/users'),

  // Partner routes (searchAdminV2-based hierarchy)
  partner: () => '/partner',
  partnerAccount: (accountId?: string) =>
    accountId ? `/partner/account/${accountId}` : '/partner/account',
  partnerEvent: (eventId?: string) =>
    eventId ? `/partner/event/${eventId}` : '/partner/event',
  partnerStore: (storeId?: string) =>
    storeId ? `/partner/store/${storeId}` : '/partner/store',
  partnerUserCheckouts: (
    externalUserId: string,
    storeId: string,
    from?: string,
  ) =>
    `/partner/user-checkouts?externalUserId=${encodeURIComponent(externalUserId)}&storeId=${encodeURIComponent(storeId)}${from ? `&from=${encodeURIComponent(from)}` : ''}`,
  partnerIntegrationWebhooks: (
    service: string,
    storeId: string,
    from?: string,
    name?: string,
  ) =>
    `/partner/integration-webhooks?service=${encodeURIComponent(service)}&storeId=${encodeURIComponent(storeId)}${name ? `&name=${encodeURIComponent(name)}` : ''}${from ? `&from=${encodeURIComponent(from)}` : ''}`,
  partnerMerchants: () => '/partner/merchants',

  // Legacy brand management (pre-searchAdminV2)
  legacyBrandSearch: () => '/legacy/brands',
  legacyBrandEdit: (brandId: string) => `/legacy/brand-edit/${brandId}`,

  // Deprecated aliases - kept so existing internal links and bookmarks keep working.
  // Route registration maps these to the new legacy pages.
  partnerBrand: (brandId?: string) =>
    brandId ? `/partner/brand/${brandId}` : '/partner/brand',
  partnerBrandStore: (brandId?: string, storeId?: string) => {
    let path = '/partner/brand';
    if (brandId) path += `/${brandId}`;
    if (storeId) path += `/store/${storeId}`;
    return path;
  },
  partnerBrandEdit: (brandId: string) => `/partner/brand-edit/${brandId}`,

  // Orders
  adminOrder: (orderId: string) => `/pre-order/${orderId}`,
  partnerSelfSignup: (partnerSelfSignupId: string) =>
    `/partner-self-signup/${partnerSelfSignupId}`,

  // Stores
  storeDetail: (storeId: string) => `/de/store/${storeId}`,
  store: (storeId: string) => `/store/${storeId}`,
  stores: () => '/stores',
  storeMap: () => '/storemap/storemap',
  // hidden, not in the sidebar: the live stadium stock plan
  stadiumStock: () => '/stadium-stock',
  // hidden, not in the sidebar: the Viva con Agua return station kiosk
  returnStation: () => '/return-station',
  // hidden, not in the sidebar: the washing hub TV following one eurobox
  washingHub: () => '/washing-hub',

  // Containers
  containers: () => '/containers',
  container: (containerId?: string) =>
    containerId ? `/container/${containerId}` : '/container',

  // Events
  events: () => '/events',
  eventOverview: () => '/event-overview',
  createEvent: () => '/event/create',
  editEvent: (eventId: string) => `/event/edit/${eventId}`,
  eventDetail: (eventId: string) => `/event/${eventId}`,
  eventDetailGeneralInfo: (eventId: string) => `/event/${eventId}/general-info`,
  eventDetailPrices: (eventId: string) => `/event/${eventId}/prices`,
  eventDetailAllowedTypes: (eventId: string) =>
    `/event/${eventId}/allowed-types`,
  eventDetailEventAccounts: (eventId: string) =>
    `/event/${eventId}/event-accounts`,
  eventDetailInvoiceInfo: (eventId: string) => `/event/${eventId}/invoice-info`,
  eventDetailDownloads: (eventId: string) => `/event/${eventId}/downloads`,
  eventDetailDeliveryAddresses: (eventId: string) =>
    `/event/${eventId}/delivery-addresses`,
  eventDetailOrderForms: (eventId: string) => `/event/${eventId}/order-forms`,
  eventDetailItemLocations: (eventId: string) =>
    `/event/${eventId}/item-locations`,
  eventDetailOrderFulfillments: (eventId: string) =>
    `/event/${eventId}/order-fulfillments`,
  eventDetailFulfillmentData: (eventId: string) =>
    `/event/${eventId}/fulfillment-data`,
  eventDetailFulfillmentDataV2: (eventId: string) =>
    `/event/${eventId}/fulfillment-data-v2`,
  eventDetailLinkedUsers: (eventId: string) => `/event/${eventId}/linked-users`,
  eventDetailInvoicing: (eventId: string) => `/event/${eventId}/invoicing`,
  eventDetailStockOverview: (eventId: string) =>
    `/event/${eventId}/stock-overview`,
  eventDetailStockOverviewOld: (eventId: string) =>
    `/event/${eventId}/stock-overview-old`,
  eventAccounts: () => '/event-accounts',
  createEventAccount: () => '/event-account/create',
  editEventAccount: (eventAccountId: string) =>
    `/event-account/edit/${eventAccountId}`,
  eventAccountDetail: (eventAccountId: string) =>
    `/event-account/${eventAccountId}`,
  partnerAccountDetail: (partnerAccountId: string) =>
    `/partner-account/${partnerAccountId}`,
  eventAccountDepositClearing: (eventId: string, eventAccountId?: string) =>
    `/event-account-deposit-clearing/${eventId}/${eventAccountId}`,
  eventOrder: (formId?: string) => `/event-order/${formId || ''}`,
  eventDetailOrders: (eventId: string, eventAccountId?: string) => {
    const path = `/event/${eventId}/orders`;
    return eventAccountId ? `${path}?eventAccountId=${eventAccountId}` : path;
  },
  eventOrderDetail: (eventId: string, orderId: string) =>
    `/event/${eventId}/orders/${orderId}`,

  // News & Marketing
  news: () => '/news',
  selectedNews: (newsContentId: string) => `/news/selected/${newsContentId}`,
  campaigns: () => '/campaigns',
  campaign: (campaignId: string) => `/campaign/${campaignId}`,

  // Finance & Billing
  finance: () => '/finance',
  financeReportsDownload: () => '/finance/reports/download',
  accountLedgerCsvUpload: () => '/finance/account-ledger-csv-upload',
  financeBulkImport: () => '/finance/bulk-import',
  monthlyInvoicing: () => '/monthly-invoicing',
  monthlyInvoicingRun: (runId: string) => `/monthly-invoicing/run/${runId}`,

  // Raffle Management
  sweepstakesManagement: () => '/sweepstakes-management',
  sweepstakesManagementCampaign: (campaignId: string) =>
    `/sweepstakes-management/${campaignId}`,
  billing: (page?: string, showPaid?: string) => {
    let path = '/billing';
    if (page) path += `/${page}`;
    if (showPaid) path += `/${showPaid}`;
    return path;
  },
  bill: (type: string, billId: string) => `/bill/${type}/${billId}`,
  invoiceCollections: () => '/invoice-collections',
  invoiceCollection: (id: string) => `/invoice-collection/${id}`,
  editCustomBillomatArticles: () => '/edit-custom-billomat-articles',

  // Admin Configuration
  config: () => '/config',
  merchantOrderConfig: () => '/merchant-order-config',
  carrierCountryConfig: () => '/config/carrier-country',
  warehouseSettings: () => '/config/warehouse-settings',
  editContainerPrices: () => '/edit-container-prices',
  editContainerInfo: () => '/edit-container-info',
  editContainerTypes: () => '/edit-container-types',
  editItemTypes: () => '/edit-item-types',
  editContainerTypeGroups: () => '/edit-container-type-groups',
  editContainerTypeGroupsBeta: () => '/edit-container-type-groups-beta',
  regions: () => '/regions',
  selfSignupConfigs: () => '/self-signup-configs',
  selfSignupConfig: (configId: string) => `/self-signup-config/${configId}`,
  editMerchantOrderItemOptions: () => '/edit-merchant-order-item-options/',
  grafanaQueries: () => '/grafana-queries',
  riderArea: () => '/rider-area',

  // Service Packages
  servicePackages: () => '/service-packages',
  servicePackage: (currency?: string, packageType?: string) =>
    currency && packageType
      ? `/service-packages/${currency}/${packageType}`
      : '/service-packages/:currency/:packageType',

  // Assets & Items
  storeAssets: () => '/assets',
  trackableItem: (itemId: string) => `/trackable-item/${itemId}`,
  eventAssetSearch: () => '/event-asset-search',

  // Inventory
  inventoryCheck: (id: string) => `/inventory-check/${id}`,
  inventoryChecks: () => '/inventory-checks',

  // Orders
  orders: () => '/orders',
  orderDetails: (orderId: string) => `/orders/${orderId}`,

  // Picking
  pickingJobs: () => '/picking-jobs',
  pickingJobDetails: (pickingJobId: string) => `/picking-jobs/${pickingJobId}`,

  editEventMerchantOrderSharedLink: (eventId: string, sharedLinkId: string) =>
    `/event/${eventId}/orders/shared-link/${sharedLinkId}`,
  createOrder: (vytalId: string) => `/create-order/${vytalId}`,

  // Container Collections
  addContainerCollections: () => '/add-container-collections',
  containerCollectionByLocation: () => '/container-collection-by-location/',
  containerCollection: (id: string) => `/container-collection/${id}`,

  // Container Stations
  containerStationSearch: () => '/container-station-search',
  containerStation: (stationId: string) => `/container-station/${stationId}`,

  // Container Types
  containerTypes: () => '/container-types',

  // Item Locations
  itemLocationSearch: () => '/item-location-search',
  itemLocation: (locationId: string) => `/item-location/${locationId}`,

  // Vending Machines
  vendingMachine: (pickpackId: string, vendingMachineId: string) =>
    `/vending-machine/${pickpackId}/${vendingMachineId}`,

  // Container QR
  containerQrRedirect: () => '/container-qr-redirect',

  // Order Fulfillment
  orderFulfillmentSearch: () => '/order-fulfillment-search',
  orderFulfillment: (fulfillmentId: string) =>
    `/order-fulfillment/${fulfillmentId}`,
  createOrderFulfillment: (timestamp?: string) =>
    timestamp
      ? `/create-order-fulfillment/${timestamp}`
      : '/create-order-fulfillment',
  unlinkedOrderFulfillmentPictures: () =>
    '/unlinked-order-fulfillment-pictures',

  // Authorized Signers
  authorizedSigner: (authorizedSignerId: string) =>
    `/authorized-signer/${authorizedSignerId}`,

  // Scan Tunnels
  scanTunnels: () => '/scan-tunnels',
  createScanTunnel: () => '/scan-tunnel/create',
  editScanTunnel: (scanTunnelId: string) => `/scan-tunnel/edit/${scanTunnelId}`,
  scanTunnel: (scanTunnelId: string) => `/scan-tunnel/${scanTunnelId}`,
  scanTunnelSession: (scanTunnelSessionId: string) =>
    `/scan-tunnel-session/${scanTunnelSessionId}`,

  // Events Dashboard
  eventsDashboardHome: (eventAccountId: string) =>
    `/events-dashboard-home/${eventAccountId}`,
  eventsDashboardInventory: (eventAccountId: string, eventId: string) =>
    `/events-dashboard-event/${eventAccountId}/${eventId}/inventory`,
  eventsDashboardFulfillments: (eventAccountId: string, eventId: string) =>
    `/events-dashboard-event/${eventAccountId}/${eventId}/fulfillments`,
  eventsDashboardSettings: (eventAccountId: string, eventId: string) =>
    `/events-dashboard-event/${eventAccountId}/${eventId}/settings`,

  // Event Owner Dashboard
  eventOwnerDashboard: (eventId?: string) =>
    `/event-owner-dashboard/${eventId || ''}`,
  eventOwnerDashboardFulfillments: (eventId: string) =>
    eventId
      ? `/event-owner-dashboard/${eventId}/fulfillments`
      : '/event-owner-dashboard',
  eventOwnerDashboardStockOverview: (eventId: string) =>
    eventId
      ? `/event-owner-dashboard/${eventId}/stock-overview`
      : '/event-owner-dashboard',

  // App Features
  appTranslations: () => '/app-translations',
  // The living component library (webapps/admin/DESIGN.md)
  design: () => '/design',
  createCheckouts: () => '/create-checkouts',
  integrationApiTools: () => '/integration-api-tools',
  devSeeding: () => '/dev-seeding',
  analyticsUsers: () => '/analytics-user',
  opsWarnings: () => '/ops-warnings',
  miniReturners: () => '/mini-returners',

  // Redirects
  legacyMerchantOrders: () => '/merchant-orders',
  legacyMerchantOrder: (orderId: string) => `/merchant-order/${orderId}`,
  pickpackStores: (storeId: string) => `/pickpack/stores/${storeId}`,
  pickpackBrands: (brandId: string) => `/pickpack/brands/${brandId}`,
  merchants: (merchantId?: string, storeId?: string) => {
    let path = '/merchants';
    if (merchantId) path += `/${merchantId}`;
    if (storeId) path += `/${storeId}`;
    return path;
  },
  salesforceStores: (storeId: string) => `/salesforce/stores/${storeId}`,

  // Misc
  eventAccount: () => '/event-account',
  event: () => '/event',
  notFound: () => '/not-found',
  paymentSelect: () => '/payment-select',
  checkout: () => '/checkout',
  pickup: () => '/pickup',

  // Partner App Redirect (public)
  partnerAppSelect: () => '/partner-app-select',

  // Hidden: Zoho deal quote builder — reachable by URL only (obfuscated suffix,
  // deliberately NOT added to AppSidebar). Takes the Zoho Deal id in the URL.
  dealQuote: (zohoDealId?: string) =>
    zohoDealId ? `/deal-quote-x7k2q/${zohoDealId}` : '/deal-quote-x7k2q',
} as const;

/**
 * Route patterns that render WITHOUT the authenticated app chrome (role nav,
 * collapse rail, logout). Single source of truth for "is this a public page" —
 * keep it next to the route definitions so adding a public route here is the
 * one place that drives sidebar/header behavior (see useIsPublicPage).
 */
export const PUBLIC_ROUTES = [
  Routes.eventOrder(':formId?'),
  Routes.partnerAppSelect(),
];
